import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { apiJson } from '../../api/client';
import { ApiError } from '../../api/errors';
import { mappedTerm, termForField } from './fieldTerms';
import { isSettledStatus } from './labels';

/*
 * Довідник понять (словник скорочень і термінів фірми): GET /api/v1/kb/glossary і /kb/glossary/resolve.
 * Запити — через apiJson. Відповіді нормалізуються: поля бази понять (explanation, fieldKeys, sources, examples,
 * category, ownerQuestion, criteria, related, statusLabel) необов'язкові, і відсутність будь-якого поля
 * не ламає інтерфейс.
 */

/** Поле-джерело після нормалізації: назва, адреса (якщо є) і уточнення (розділ, пункт). */
export type GlossarySource = { title: string; url: string | null; note: string | null };

/** Приклад уживання: необов'язковий заголовок і текст (підмножина Markdown). */
export type GlossaryExample = { title: string | null; text: string };

export type GlossaryTerm = {
  id: string;
  term: string;
  definition: string;
  synonyms: string[];
  /** abbreviation | term | (згодом інші види); невідомий вид показується як «Інше». */
  kind: string;
  /** official_list | other_documents | internal_term | system */
  origin: string | null;
  /** confirmed | draft | owner_decision_needed | deprecated (раніше — active | ambiguous | unknown) */
  status: string;
  businessRoleCode: string | null;
  sourceRef: string | null;
  notes: string | null;
  matchedBy: 'term' | 'synonym' | 'text' | null;
  archivedAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  /** Докладне пояснення (Markdown). */
  explanation?: string;
  /** Ключі полів інтерфейсу, до яких прив'язано поняття (див. fieldTerms.ts). */
  fieldKeys?: string[];
  sources?: GlossarySource[];
  examples?: GlossaryExample[];
  /** Підпис статусу від сервера (confirmed → «Підтверджено»…). */
  statusLabel?: string;
  /** Категорія поняття (для групування й фільтра). */
  category?: string;
  /** Питання власнику, якщо визначення немає або джерела розходяться (статус owner_decision_needed). */
  ownerQuestion?: string;
  /** Критерії застосування (ознаки). */
  criteria?: string[];
  /** Пов'язані поняття (терміни словника). */
  related?: string[];
};

export type GlossaryResolution = {
  term: string;
  canonical: GlossaryTerm | null;
  matches: GlossaryTerm[];
  /** Тимчасовий збій (мережа, сервер): результат порожній і швидко застаріває. */
  failed?: boolean;
};

export type GlossaryIndex = {
  terms: GlossaryTerm[];
  /** Ключ поля → термін (з поля fieldKeys понять). */
  byFieldKey: ReadonlyMap<string, string>;
  /** Словник більший за межу завантаження — решту знаходить пошук. */
  truncated: boolean;
};

const BASE = '/api/v1/kb/glossary';
const PAGE_LIMIT = 100;
const MAX_PAGES = 20;
const TEN_MINUTES = 10 * 60_000;
const QUERY_MAX = 100;

export const knowledgeKeys = {
  all: ['kb', 'glossary'] as const,
  index: ['kb', 'glossary', 'index'] as const,
  search: (q: string) => ['kb', 'glossary', 'search', q] as const,
  resolve: (term: string) => ['kb', 'glossary', 'resolve', term] as const,
};

// ——— Нормалізація відповіді ———

const text = (v: unknown): string | null => {
  if (typeof v !== 'string') return null;
  const s = v.trim();
  return s ? s : null;
};

const textList = (v: unknown): string[] =>
  Array.isArray(v) ? v.map(text).filter((s): s is string => s !== null) : [];

function sourceFromString(s: string): GlossarySource {
  return /^https?:\/\//i.test(s) ? { title: s, url: s, note: null } : { title: s, url: null, note: null };
}

function normalizeSources(v: unknown): GlossarySource[] {
  const list: unknown[] = Array.isArray(v) ? v : typeof v === 'string' ? [v] : [];
  const out: GlossarySource[] = [];
  for (const item of list) {
    const s = text(item);
    if (s) {
      out.push(sourceFromString(s));
      continue;
    }
    if (item && typeof item === 'object') {
      const o = item as Record<string, unknown>;
      // Документ реєстру без зовнішньої адреси — посилання на його картку в ЄРС.
      const docId = text(o.documentId);
      const url = text(o.url) ?? text(o.href) ?? text(o.link) ?? (docId ? `/kb/registry/documents/${encodeURIComponent(docId)}` : null);
      const title = text(o.title) ?? text(o.label) ?? text(o.name) ?? text(o.ref) ?? text(o.code) ?? url;
      const parts = [text(o.documentCode), text(o.section), text(o.ref) !== title ? text(o.ref) : null].filter((x): x is string => !!x);
      if (title) out.push({ title, url, note: text(o.note) ?? (parts.length ? parts.join(' · ') : text(o.description)) });
    }
  }
  return out.slice(0, 50);
}

function normalizeExamples(v: unknown): GlossaryExample[] {
  const list: unknown[] = Array.isArray(v) ? v : typeof v === 'string' ? [v] : [];
  const out: GlossaryExample[] = [];
  for (const item of list) {
    const s = text(item);
    if (s) {
      out.push({ title: null, text: s });
      continue;
    }
    if (item && typeof item === 'object') {
      const o = item as Record<string, unknown>;
      const body = text(o.text) ?? text(o.body) ?? text(o.example) ?? text(o.content) ?? text(o.value);
      if (body) out.push({ title: text(o.title) ?? text(o.label), text: body });
    }
  }
  return out.slice(0, 30);
}

/** Поняття з відповіді сервера → GlossaryTerm; null, якщо немає навіть терміна. */
export function normalizeTerm(raw: unknown): GlossaryTerm | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const term = text(r.term);
  if (!term) return null;
  const matchedBy = r.matchedBy === 'term' || r.matchedBy === 'synonym' || r.matchedBy === 'text' ? r.matchedBy : null;
  const t: GlossaryTerm = {
    id: text(r.id) ?? `term:${term}`,
    term,
    definition: text(r.definition) ?? '',
    synonyms: textList(r.synonyms),
    kind: text(r.kind) ?? 'other',
    origin: text(r.origin),
    status: text(r.status) ?? 'active',
    businessRoleCode: text(r.businessRoleCode),
    sourceRef: text(r.sourceRef),
    notes: text(r.notes),
    matchedBy,
    archivedAt: text(r.archivedAt),
    createdAt: text(r.createdAt),
    updatedAt: text(r.updatedAt),
  };
  const explanation = text(r.explanation);
  if (explanation) t.explanation = explanation;
  const statusLabelText = text(r.statusLabel);
  if (statusLabelText) t.statusLabel = statusLabelText;
  const category = text(r.category);
  if (category) t.category = category;
  const ownerQuestion = text(r.ownerQuestion);
  if (ownerQuestion) t.ownerQuestion = ownerQuestion;
  if (r.criteria !== undefined && r.criteria !== null) t.criteria = textList(r.criteria);
  if (r.related !== undefined && r.related !== null) t.related = textList(r.related);
  if (r.fieldKeys !== undefined && r.fieldKeys !== null) t.fieldKeys = textList(r.fieldKeys);
  const sources = normalizeSources(r.sources);
  if (sources.length) t.sources = sources;
  const examples = normalizeExamples(r.examples);
  if (examples.length) t.examples = examples;
  return t;
}

function toPage(raw: unknown): { data: GlossaryTerm[]; nextCursor: string | null } {
  const r = (raw && typeof raw === 'object' ? raw : {}) as { data?: unknown; nextCursor?: unknown };
  const data = Array.isArray(r.data) ? r.data.map(normalizeTerm).filter((t): t is GlossaryTerm => t !== null) : [];
  return { data, nextCursor: typeof r.nextCursor === 'string' && r.nextCursor ? r.nextCursor : null };
}

/** Ключ поля → термін з поля fieldKeys (чинні поняття мають перевагу). */
export function buildFieldKeyIndex(terms: GlossaryTerm[]): Map<string, string> {
  const map = new Map<string, string>();
  const ordered = [...terms].sort((a, b) => Number(!isSettledStatus(a.status)) - Number(!isSettledStatus(b.status)));
  for (const t of ordered) for (const key of t.fieldKeys ?? []) if (!map.has(key)) map.set(key, t.term);
  return map;
}

// ——— Запити ———

const isAbort = (e: unknown) => e instanceof DOMException && e.name === 'AbortError';

/** Пошук за терміном, синонімом і текстом визначення (ранжує сервер). */
export async function searchGlossary(q: string, opts: { limit?: number; signal?: AbortSignal } = {}): Promise<GlossaryTerm[]> {
  const params = new URLSearchParams({ q: q.trim().slice(0, QUERY_MAX), limit: String(opts.limit ?? PAGE_LIMIT) });
  return toPage(await apiJson<unknown>('GET', `${BASE}?${params}`, undefined, { signal: opts.signal })).data;
}

/** Увесь словник (посторінково, до MAX_PAGES сторінок) і покажчик ключів полів. */
export async function fetchGlossaryIndex(signal?: AbortSignal): Promise<GlossaryIndex> {
  const terms: GlossaryTerm[] = [];
  const seen = new Set<string>();
  let cursor: string | null = null;
  let pages = 0;
  do {
    const params = new URLSearchParams({ limit: String(PAGE_LIMIT) });
    if (cursor) params.set('cursor', cursor);
    const page = toPage(await apiJson<unknown>('GET', `${BASE}?${params}`, undefined, { signal }));
    for (const t of page.data) {
      if (seen.has(t.id)) continue;
      seen.add(t.id);
      terms.push(t);
    }
    cursor = page.nextCursor && page.nextCursor !== cursor ? page.nextCursor : null;
    pages += 1;
  } while (cursor && pages < MAX_PAGES);
  return { terms, byFieldKey: buildFieldKeyIndex(terms), truncated: cursor !== null };
}

/** Після 403/404 (немає прав чи маршруту) не питаємо сервер 10 хв — підказки просто не показуються. */
let resolveOffUntil = 0;

/** Основний термін для назви («РОП» → «КВП»). Будь-яка помилка — порожній результат, а не збій. */
export async function resolveGlossaryTerm(term: string, signal?: AbortSignal): Promise<GlossaryResolution> {
  const t = term.trim().slice(0, QUERY_MAX);
  const empty = (failed = false): GlossaryResolution => ({ term: t, canonical: null, matches: [], ...(failed ? { failed } : {}) });
  if (!t || Date.now() < resolveOffUntil) return empty();
  try {
    const raw = await apiJson<unknown>('GET', `${BASE}/resolve?${new URLSearchParams({ term: t })}`, undefined, { signal });
    const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
    const matches = Array.isArray(r.matches) ? r.matches.map(normalizeTerm).filter((x): x is GlossaryTerm => x !== null) : [];
    return { term: t, canonical: normalizeTerm(r.canonical) ?? matches[0] ?? null, matches };
  } catch (e) {
    if (isAbort(e)) throw e;
    if (e instanceof ApiError && e.status >= 400 && e.status < 500) {
      if (e.status === 403 || e.status === 404) resolveOffUntil = Date.now() + TEN_MINUTES;
      return empty();
    }
    return empty(true);
  }
}

// ——— Хуки ———

/**
 * Поняття за назвою (терміном або синонімом): кеш 10 хв, без повторів.
 * Помилки не кидаються — `data.canonical` просто null.
 */
export function useTermResolve(term: string | null | undefined) {
  const t = (term ?? '').trim().slice(0, QUERY_MAX);
  return useQuery({
    queryKey: knowledgeKeys.resolve(t.toLocaleLowerCase('uk-UA')),
    queryFn: ({ signal }) => resolveGlossaryTerm(t, signal),
    enabled: t.length > 0,
    staleTime: (query) => (query.state.data?.failed ? 30_000 : TEN_MINUTES),
    gcTime: 3 * TEN_MINUTES,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

/**
 * Увесь словник (для довідника й покажчика fieldKeys): кеш 10 хв, без повторів.
 * `quiet` — для підказок: після помилки не повторювати запит під час кожного монтування.
 */
export function useGlossaryIndex(opts: { enabled?: boolean; quiet?: boolean } = {}) {
  return useQuery({
    queryKey: knowledgeKeys.index,
    queryFn: ({ signal }) => fetchGlossaryIndex(signal),
    enabled: opts.enabled ?? true,
    staleTime: TEN_MINUTES,
    gcTime: 3 * TEN_MINUTES,
    retry: false,
    retryOnMount: !opts.quiet,
    refetchOnWindowFocus: false,
  });
}

/** Пошук понять (сервер ранжує: точний термін, синонім, текст визначення). Попередні результати тримаються до нових. */
export function useGlossarySearch(q: string, opts: { enabled?: boolean; limit?: number } = {}) {
  const term = q.trim().slice(0, QUERY_MAX);
  const limit = opts.limit ?? PAGE_LIMIT;
  return useQuery({
    queryKey: [...knowledgeKeys.search(term.toLocaleLowerCase('uk-UA')), limit],
    queryFn: ({ signal }) => searchGlossary(term, { limit, signal }),
    enabled: (opts.enabled ?? true) && term.length > 0,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
    retry: false,
  });
}

/**
 * Термін для ключа поля (див. fieldTerms.ts). Список понять із сервера запитується лише тоді,
 * коли ключа немає в мапі FIELD_TERMS (щоб урахувати майбутнє поле fieldKeys); до відповіді — null.
 */
export function useFieldTerm(fieldKey: string | null | undefined): string | null {
  const key = (fieldKey ?? '').trim();
  const direct = key ? mappedTerm(key) : null;
  const needServer = key.length > 0 && !direct;
  const index = useGlossaryIndex({ enabled: needServer, quiet: true });
  if (!key) return null;
  if (direct) return direct;
  if (index.isLoading) return null;
  return termForField(key, index.data?.byFieldKey);
}

/** Адреса поняття в довіднику. */
export function termHref(term: string): string {
  return `/knowledge/terms?${new URLSearchParams({ term })}`;
}
